import config from '@haaretz/l-config';
import usePlatform from '@haaretz/s-atoms/platform';
import usePreviewId from '@haaretz/s-atoms/previewId';
import useRenderingKind from '@haaretz/s-atoms/renderingKind';
import discardFields from '@haaretz/s-common-utils/discardFields';
import discardNullableValues from '@haaretz/s-common-utils/discardNullableValues';
import isTeaserBiData from '@haaretz/s-type-predicates/isTeaserBiData';
import useLoadEvent from '@haaretz/s-use-native-application/useLoadEvent';
import useOnce from '@haaretz/s-use-once';
import React from 'react';
import { useBiPageviewOverrides } from './BiPageviewOverrides';
import getBiData from './helpers/getBiData';
import getTeaserBiData from './helpers/getTeaserBiData';
import useBiDataContext from './useBiDataContext';
const appType = config.get('appType');
const biServer = config.get('bIServer');
export default function useBi(biType = 'action') {
    const contextBiData = useBiDataContext();
    const isLoaded = useLoadEvent();
    const platform = usePlatform();
    const isPreview = !!usePreviewId();
    const renderingKind = useRenderingKind();
    const bufferedBiEvents = React.useRef([]);
    const lastSentPageviewKey = React.useRef(null);
    const biPageviewOverrides = useBiPageviewOverrides();
    const collectData = React.useCallback((originUserBiData, options)=>{
        if (isPreview) {
            return;
        }
        const userBiData = isTeaserBiData(originUserBiData) ? getTeaserBiData(originUserBiData, biType) : getBiData(originUserBiData);
        if (!isLoaded) {
            if (biType !== 'pageview') {
                bufferedBiEvents.current.push(userBiData);
            }
            return;
        }
        let biUrl;
        let pageviewKey;
        switch(biType){
            case 'impression':
                biUrl = `${biServer}/impressionPlain`;
                break;
            case 'pageview':
                {
                    pageviewKey = options?.pageviewKey || // NOTE: Namespace a virtual pageview key (e.g. `messages`) with the
                    // page url so the same key can't collide across pages, and so each
                    // virtual pageview stays distinct from the page's base pageview.
                    biPageviewOverrides?.pageviewKey && `${contextBiData.url}#${biPageviewOverrides.pageviewKey}` || contextBiData.url;
                    if (pageviewKey && lastSentPageviewKey.current === pageviewKey && appType !== 'login') {
                        // NOTE: Early return if this pageview was already sent to BI
                        return;
                    }
                    biUrl = `${biServer}/requestPlain`;
                    if (contextBiData.article_id) {
                        // NOTE: Add articleId to the URL for pageview for better BI debugging
                        biUrl = `${biUrl}?articleId=${encodeURIComponent(contextBiData.article_id)}`;
                    }
                    break;
                }
            default:
                biUrl = `${biServer}/actionPlain`;
        }
        const searchParams = new URLSearchParams(document.location.search);
        const additional_info = JSON.stringify({
            ...userBiData?.additional_info ?? {},
            ...searchParams.has('gift') ? {
                gift: searchParams.get('gift')
            } : {},
            ...renderingKind === 'semi-closed' ? {
                rendering_kind: renderingKind
            } : {}
        });
        let finalBiData = {
            ...contextBiData,
            ...userBiData,
            ...biPageviewOverrides?.biData,
            additional_info
        };
        if (biType !== 'action' && finalBiData) {
            finalBiData = discardFields(finalBiData, [
                'action_id'
            ]);
        }
        const payload = JSON.stringify(discardNullableValues(finalBiData));
        const beaconAccepted = window.navigator.sendBeacon(biUrl, payload);
        if (!beaconAccepted) {
            void fetch(biUrl, {
                body: payload,
                keepalive: true,
                method: 'POST'
            }).catch(()=>undefined);
        }
        if (pageviewKey) {
            lastSentPageviewKey.current = pageviewKey;
        }
    }, [
        biPageviewOverrides,
        biType,
        contextBiData,
        isLoaded,
        isPreview,
        renderingKind
    ]);
    useOnce(()=>{
        const eventsToSend = bufferedBiEvents.current.splice(0);
        eventsToSend.forEach((data)=>{
            collectData(data);
        });
    }, platform === 'app' && isLoaded && !isPreview);
    return collectData;
}

//# sourceMappingURL=useBi.js.map